import React, { useState, useRef, useCallback } from "react";
import { API_URL } from "../config";
import "./EditorKeyFrame.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

const DEFAULT_POSITIVE = "RAW photograph, photorealistic, highly detailed, sharp focus, natural lighting, professional photography, 8k uhd, film grain, dslr, high quality";
const DEFAULT_NEGATIVE = "cartoon, anime, illustration, painting, drawing, 3d render, cgi, blurry, low quality, deformed, distorted, watermark, text, plastic skin, oversaturated";

export default function EditorKeyFrame() {
  // Tabs
  const [tab, setTab] = useState("reconstruct"); // "reconstruct" | "edit"

  // Imagen de entrada
  const [inputImage, setInputImage] = useState(null); // {file, preview, name}
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  // Resultado
  const [resultImage, setResultImage] = useState(null); // base64
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Reconstruir (M01)
  const [positive, setPositive] = useState(DEFAULT_POSITIVE);
  const [sizeMode, setSizeMode] = useState("original"); // "original" | "1024" | "custom"
  const [customW, setCustomW] = useState(1024);
  const [customH, setCustomH] = useState(1024);
  const [seed, setSeed] = useState(-1);

  // Editar zona (M02+M03)
  const [editStep, setEditStep] = useState(1); // 1=segmentar, 2=inpaint
  const [objectPrompt, setObjectPrompt] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editNegative, setEditNegative] = useState("cartoon, anime, illustration, 3d render, cgi, blurry, low quality, deformed, distorted, watermark, text");
  const [maskHard, setMaskHard] = useState(null); // base64
  const [maskFeather, setMaskFeather] = useState(null); // base64

  // Historial de ediciones (hasta 5)
  const [editHistory, setEditHistory] = useState([]); // [{label, preview, b64, file}]
  const [selectedVersion, setSelectedVersion] = useState(-1); // -1 = ninguna seleccionada

  // Biblioteca picker
  const [showBiblioteca, setShowBiblioteca] = useState(false);
  const [bibImages, setBibImages] = useState([]);
  const [bibLoading, setBibLoading] = useState(false);

  // --- Helpers ---
  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setInputImage({ file, preview: e.target.result, name: file.name });
      setResultImage(null);
      setMaskHard(null);
      setMaskFeather(null);
      setEditStep(1);
      setError(null);
      setEditHistory([{ label: "Original", preview: e.target.result, file }]);
      setSelectedVersion(0);
    };
    reader.readAsDataURL(file);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const getWidth = () => sizeMode === "custom" ? customW : 1024;
  const getHeight = () => sizeMode === "custom" ? customH : 1024;

  // --- Polling helper ---
  const pollJob = async (jobId) => {
    const token = getToken();
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const res = await fetch(`${API_URL}/editor/${encodeURIComponent(jobId)}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "completed") {
        const resultRes = await fetch(`${API_URL}/editor/${encodeURIComponent(jobId)}/result`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return await resultRes.json();
      }
      if (data.status === "error") {
        throw new Error(data.error || "Error en la generacion");
      }
    }
    throw new Error("Timeout: la generacion tardo demasiado");
  };

  // --- M01: Reconstruir ---
  const handleReconstruct = async () => {
    if (!inputImage) return;
    setProcessing(true);
    setError(null);
    setResultImage(null);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("image", inputImage.file);
      formData.append("positive", positive);
      formData.append("negative", DEFAULT_NEGATIVE);
      formData.append("size_mode", sizeMode);
      formData.append("width", getWidth());
      formData.append("height", getHeight());
      formData.append("seed", seed);

      const res = await fetch(`${API_URL}/editor/reconstruct`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al enviar");

      const result = await pollJob(data.job_id);
      setResultImage(result.image);
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  // --- M02: Segmentar ---
  const handleSegment = async () => {
    if (!inputImage || !objectPrompt.trim()) return;
    setProcessing(true);
    setError(null);
    setMaskHard(null);
    setMaskFeather(null);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("image", inputImage.file);
      formData.append("object_prompt", objectPrompt.trim());

      const res = await fetch(`${API_URL}/editor/segment`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al segmentar");

      const result = await pollJob(data.job_id);
      setMaskHard(result.mask_hard || null);
      setMaskFeather(result.mask_feather || null);
      setEditStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  // --- M03: Inpaint ---
  const handleInpaint = async () => {
    if (!inputImage || !maskFeather || !editPrompt.trim()) return;
    setProcessing(true);
    setError(null);
    setResultImage(null);

    try {
      const token = getToken();

      // Convertir mascara base64 a File
      const maskBlob = await fetch(`data:image/png;base64,${maskFeather}`).then(r => r.blob());
      const maskFile = new File([maskBlob], "mask_feather.png", { type: "image/png" });

      const formData = new FormData();
      formData.append("image", inputImage.file);
      formData.append("mask", maskFile);
      formData.append("positive", editPrompt.trim());
      formData.append("negative", editNegative.trim());
      formData.append("size_mode", sizeMode);
      formData.append("width", getWidth());
      formData.append("height", getHeight());
      formData.append("seed", seed);

      const res = await fetch(`${API_URL}/editor/inpaint`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al inpaintar");

      const result = await pollJob(data.job_id);
      setResultImage(result.image);
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  // --- Usar resultado como nueva entrada ---
  const handleUseAsInput = async () => {
    if (!resultImage) return;
    const blob = await fetch(`data:image/png;base64,${resultImage}`).then(r => r.blob());
    const file = new File([blob], `edited_${Date.now()}.png`, { type: "image/png" });
    const preview = `data:image/png;base64,${resultImage}`;

    // Añadir al historial (max 5)
    const newHistory = [...editHistory, { label: `Edicion ${editHistory.length}`, preview, file, b64: resultImage }];
    if (newHistory.length > 5) newHistory.shift();
    setEditHistory(newHistory);
    setSelectedVersion(newHistory.length - 1);

    setInputImage({ file, preview, name: file.name });
    setResultImage(null);
    setMaskHard(null);
    setMaskFeather(null);
    setEditStep(1);
    setError(null);
  };

  // --- Biblioteca picker ---
  const openBiblioteca = async () => {
    setShowBiblioteca(true);
    setBibLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/images?per_page=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBibImages(data.items || []);
      }
    } catch (e) { /* ignore */ }
    setBibLoading(false);
  };

  const selectFromBiblioteca = async (img) => {
    setShowBiblioteca(false);
    try {
      const url = img.png_path || img.url;
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], `bib_${img.id}.png`, { type: "image/png" });
      const reader = new FileReader();
      reader.onload = (e) => {
        setInputImage({ file, preview: e.target.result, name: file.name });
        setResultImage(null);
        setMaskHard(null);
        setMaskFeather(null);
        setEditStep(1);
        setError(null);
        setEditHistory([{ label: "Original", preview: e.target.result, file }]);
        setSelectedVersion(0);
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      setError("Error al cargar imagen: " + e.message);
    }
  };

  // --- Seleccionar version del historial ---
  const handleSelectVersion = (idx) => {
    setSelectedVersion(idx);
    const version = editHistory[idx];
    if (version) {
      setResultImage(version.b64 || null);
      if (idx === editHistory.length - 1) {
        // Si es la última, la ponemos como input para seguir editando
        setInputImage({ file: version.file, preview: version.preview, name: version.file?.name || "version.png" });
      }
    }
  };

  // --- Eliminar última version ---
  const handleDeleteLastVersion = () => {
    if (editHistory.length <= 1) return;
    const newHistory = editHistory.slice(0, -1);
    setEditHistory(newHistory);
    const lastIdx = newHistory.length - 1;
    setSelectedVersion(lastIdx);
    const prev = newHistory[lastIdx];
    setInputImage({ file: prev.file, preview: prev.preview, name: prev.file?.name || "version.png" });
    setResultImage(prev.b64 || null);
    setMaskHard(null);
    setMaskFeather(null);
    setEditStep(1);
  };

  // --- Download ---
  const handleDownload = () => {
    if (!resultImage) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${resultImage}`;
    link.download = `editor_${tab}_${Date.now()}.png`;
    link.click();
  };

  // --- Guardar en Biblioteca ---
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const handleSaveToBiblioteca = async () => {
    if (!resultImage || saving) return;
    setSaving(true);
    try {
      const token = getToken();
      const blob = await fetch(`data:image/png;base64,${resultImage}`).then(r => r.blob());
      const file = new File([blob], `editor_${tab}_${Date.now()}.png`, { type: "image/png" });
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/images/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e) {
      setError("Error al guardar: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // --- Render ---
  return (
    <div className="ekf-page">
      {/* Panel izquierdo: controles + imagen original */}
      <div className="ekf-panel--left">
        {/* Tabs */}
        <div className="ekf-tabs">
          <button className={`ekf-tab ${tab === "reconstruct" ? "active" : ""}`} onClick={() => { setTab("reconstruct"); setResultImage(null); setError(null); }}>
            Reconstruir
          </button>
          <button className={`ekf-tab ${tab === "edit" ? "active" : ""}`} onClick={() => { setTab("edit"); setResultImage(null); setError(null); }}>
            Editar Zona
          </button>
        </div>

        {/* Drop zone / imagen original */}
        <div
          className={`ekf-dropzone ${dragging ? "dragging" : ""} ${inputImage ? "ekf-dropzone-has-image" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          {inputImage ? (
            <>
              <img src={inputImage.preview} alt="Input" />
              <span style={{ fontSize: 11, opacity: 0.6 }}>{inputImage.name} — click para cambiar</span>
            </>
          ) : (
            <>
              <div className="ekf-dropzone-icon">+</div>
              <div>Arrastra una imagen o haz click</div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
        <button className="ekf-btn-biblioteca-pick" onClick={openBiblioteca}>
          Desde Biblioteca
        </button>

        {/* Selector de tamanio */}
        <div className="ekf-section">
          <div className="ekf-section-title">Tamanio de salida</div>
          <div className="ekf-size-options">
            <button className={`ekf-size-btn ${sizeMode === "original" ? "active" : ""}`} onClick={() => setSizeMode("original")}>Original</button>
            <button className={`ekf-size-btn ${sizeMode === "1024" ? "active" : ""}`} onClick={() => setSizeMode("1024")}>1024x1024</button>
            <button className={`ekf-size-btn ${sizeMode === "custom" ? "active" : ""}`} onClick={() => setSizeMode("custom")}>Personalizado</button>
          </div>
          {sizeMode === "custom" && (
            <div className="ekf-custom-size">
              <input type="number" value={customW} onChange={(e) => setCustomW(Number(e.target.value))} placeholder="Ancho" />
              <span style={{ color: "rgba(255,255,255,0.4)", alignSelf: "center" }}>x</span>
              <input type="number" value={customH} onChange={(e) => setCustomH(Number(e.target.value))} placeholder="Alto" />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(255,60,60,0.15)", color: "#ff6b6b", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* --- Tab: Reconstruir --- */}
        {tab === "reconstruct" && (
          <>
            <div className="ekf-section">
              <div className="ekf-section-title">Prompt positivo</div>
              <textarea
                className="ekf-textarea"
                value={positive}
                onChange={(e) => setPositive(e.target.value)}
                rows={3}
                placeholder="Describe la imagen..."
              />
            </div>

            <div className="ekf-section">
              <label className="ekf-label">Seed (-1 = aleatorio)</label>
              <input className="ekf-input" type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} />
            </div>

            <button
              className="ekf-btn-primary"
              disabled={!inputImage || processing}
              onClick={handleReconstruct}
            >
              {processing ? "Procesando..." : "Reconstruir"}
            </button>
          </>
        )}

        {/* --- Tab: Editar Zona --- */}
        {tab === "edit" && (
          <>
            <div className="ekf-steps">
              <div className={`ekf-step ${editStep === 1 ? "active" : editStep > 1 ? "done" : ""}`}>
                <span className="ekf-step-num">1</span>
                Segmentar
              </div>
              <span style={{ color: "rgba(255,255,255,0.2)" }}>&rarr;</span>
              <div className={`ekf-step ${editStep === 2 ? "active" : ""}`}>
                <span className="ekf-step-num">2</span>
                Editar
              </div>
            </div>

            {editStep === 1 && (
              <>
                <div className="ekf-section">
                  <div className="ekf-section-title">Objeto a seleccionar</div>
                  <input
                    className="ekf-input"
                    value={objectPrompt}
                    onChange={(e) => setObjectPrompt(e.target.value)}
                    placeholder='Escribe en ingles: "the dog", "red car", "person"...'
                  />
                </div>

                <button
                  className="ekf-btn-primary"
                  disabled={!inputImage || !objectPrompt.trim() || processing}
                  onClick={handleSegment}
                >
                  {processing ? "Segmentando..." : "Segmentar"}
                </button>
              </>
            )}

            {editStep === 2 && (
              <>
                <div className="ekf-btn-row" style={{ marginBottom: 8 }}>
                  <button className="ekf-btn-secondary" onClick={() => { setEditStep(1); setMaskHard(null); setMaskFeather(null); setResultImage(null); }}>
                    Repetir segmentacion
                  </button>
                </div>

                <div className="ekf-section">
                  <div className="ekf-section-title">Prompt positivo</div>
                  <textarea
                    className="ekf-textarea"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    rows={3}
                    placeholder='Describe lo que quieres ver: "marble floor, sunlight", "a black cat sitting"...'
                  />
                </div>

                <div className="ekf-section">
                  <div className="ekf-section-title">Prompt negativo</div>
                  <textarea
                    className="ekf-textarea"
                    value={editNegative}
                    onChange={(e) => setEditNegative(e.target.value)}
                    rows={2}
                    placeholder='Lo que NO quieres: "dog, animal, person, blurry"...'
                  />
                </div>

                <div className="ekf-section">
                  <label className="ekf-label">Seed (-1 = aleatorio)</label>
                  <input className="ekf-input" type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value))} />
                </div>

                <button
                  className="ekf-btn-primary"
                  disabled={!editPrompt.trim() || processing}
                  onClick={handleInpaint}
                >
                  {processing ? "Editando..." : "Generar"}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Panel derecho: resultado grande */}
      <div className="ekf-panel--right">
        {processing ? (
          <div className="ekf-preview">
            <div className="ekf-spinner">
              <div className="ekf-spinner-ring" />
              {tab === "reconstruct" ? "Reconstruyendo imagen..." : editStep === 1 ? "Segmentando objeto..." : "Editando zona..."}
            </div>
          </div>
        ) : resultImage ? (
          <div className="ekf-preview">
            <span className="ekf-preview-label">Resultado</span>
            <img src={`data:image/png;base64,${resultImage}`} alt="Resultado" />
            <div className="ekf-result-actions">
              <button className="ekf-btn-reusar" onClick={handleUseAsInput}>Seguir editando</button>
              <button className="ekf-btn-download" onClick={handleDownload}>Descargar</button>
              <button className="ekf-btn-biblioteca" onClick={handleSaveToBiblioteca} disabled={saving || saved}>
                {saved ? "Guardado" : saving ? "Guardando..." : "Guardar en Biblioteca"}
              </button>
            </div>
          </div>
        ) : maskFeather && tab === "edit" ? (
          <div className="ekf-preview">
            <span className="ekf-preview-label">Mascara detectada</span>
            <div className="ekf-mask-overlay">
              <img src={inputImage?.preview} alt="Original" />
              <img className="ekf-mask-img" src={`data:image/png;base64,${maskFeather}`} alt="Mascara" />
            </div>
          </div>
        ) : (
          <div className="ekf-preview">
            <div className="ekf-preview-placeholder">
              {inputImage ? "Listo para procesar" : "El resultado aparecera aqui"}
            </div>
          </div>
        )}

        {/* Cinta de versiones */}
        {editHistory.length > 0 && (
          <div className="ekf-history">
            <div className="ekf-history-strip">
              {editHistory.map((v, idx) => (
                <div
                  key={idx}
                  className={`ekf-history-item ${selectedVersion === idx ? "active" : ""}`}
                  onClick={() => handleSelectVersion(idx)}
                >
                  <img src={v.preview} alt={v.label} />
                  <span className="ekf-history-label">{v.label}</span>
                </div>
              ))}
            </div>
            {editHistory.length > 1 && (
              <button className="ekf-btn-delete-version" onClick={handleDeleteLastVersion}>
                Eliminar ultima
              </button>
            )}
          </div>
        )}
      </div>
      {/* Modal Biblioteca */}
      {showBiblioteca && (
        <div className="ekf-modal-overlay" onClick={() => setShowBiblioteca(false)}>
          <div className="ekf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ekf-modal-header">
              <span>Selecciona de Biblioteca</span>
              <button className="ekf-modal-close" onClick={() => setShowBiblioteca(false)}>x</button>
            </div>
            <div className="ekf-modal-grid">
              {bibLoading ? (
                <div className="ekf-spinner"><div className="ekf-spinner-ring" />Cargando...</div>
              ) : bibImages.length === 0 ? (
                <div style={{ color: "#999", padding: 20 }}>No hay imagenes en la biblioteca</div>
              ) : (
                bibImages.map((img) => (
                  <div key={img.id} className="ekf-modal-thumb" onClick={() => selectFromBiblioteca(img)}>
                    <img src={img.png_path || img.url} alt={img.prompt || ""} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
